import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { vwapRequest } from '../../lib/vwapClient.js'

export const SharedAnchoredVwapContext = createContext(null)
const EMPTY = { snapshots: [], error: '' }

// One owner-scoped reader per workspace. Panes retain independent presentation
// and mutations; provider calculations and alert monitoring remain server-owned.
export default function SharedAnchoredVwap({ userId, symbol, enabled, request = vwapRequest, children }) {
  const scope = JSON.stringify([userId, symbol, Boolean(enabled)])
  const [result, setResult] = useState({ scope, ...EMPTY })
  const commands = useRef({ refresh: () => {}, invalidate: () => {} })
  const refresh = useCallback(() => commands.current.refresh(), [])
  const invalidate = useCallback(() => commands.current.invalidate(), [])
  useEffect(() => {
    let active = true, timer, deadline, controller, generation = 0
    const stopRead = () => {
      ++generation
      clearTimeout(timer); timer = null; clearTimeout(deadline); controller?.abort()
    }
    const publish = value => setResult({ scope, ...value })
    const poll = async () => {
      stopRead()
      if (!active || !enabled || !userId || !symbol) return
      const run = generation
      controller = new AbortController()
      const current = controller
      const isCurrent = () => active && run === generation && !current.signal.aborted
      deadline = setTimeout(() => {
        if (!isCurrent()) return
        stopRead()
        setResult(old => ({ ...old, scope, error: 'VWAP connection timed out' }))
        timer = setTimeout(poll, 1000)
      }, 10000)
      try {
        const value = await request({ symbol, ownerId: userId, signal: current.signal })
        if (!isCurrent()) return
        if (value?.symbol !== symbol || !Array.isArray(value.anchors)
          || value.anchors.some(a => !a || a.symbol !== symbol || typeof a.id !== 'string')) {
          throw new Error('Invalid VWAP snapshot response')
        }
        publish({ snapshots: value.anchors, error: '' })
      } catch (error) {
        if (isCurrent()) setResult(old => ({ ...old, scope, error: error.message }))
      } finally {
        if (isCurrent()) { clearTimeout(deadline); timer = setTimeout(poll, 1000) }
      }
    }
    commands.current = { refresh: poll, invalidate: stopRead }
    publish(EMPTY)
    void poll()
    // Resume idle polling immediately, but do not restart an in-flight read for
    // a burst of focus/pageshow events.
    const onResume = () => { if (!document.hidden && timer) { clearTimeout(timer); timer = null; void poll() } }
    window.addEventListener('focus', onResume); window.addEventListener('pageshow', onResume); window.addEventListener('online', onResume)
    document.addEventListener('visibilitychange', onResume)
    return () => {
      active = false; stopRead()
      commands.current = { refresh: () => {}, invalidate: () => {} }
      window.removeEventListener('focus', onResume); window.removeEventListener('pageshow', onResume); window.removeEventListener('online', onResume)
      document.removeEventListener('visibilitychange', onResume)
    }
  }, [scope, userId, symbol, enabled, request])
  const visible = result.scope === scope ? result : EMPTY
  const value = useMemo(() => ({ ...visible, ownerId: userId, refresh, invalidate }), [visible, userId, refresh, invalidate])
  return <SharedAnchoredVwapContext.Provider value={value}>{children}</SharedAnchoredVwapContext.Provider>
}
